import React, { useEffect, useState } from 'react';
import { G, styles, api } from '../theme.js';
import { Btn, Spinner } from '../ui.jsx';

// Public invoice payment page. URL: /pay/:invoiceId?t=<token>
export function InvoicePay() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);

  const segments = window.location.pathname.split('/').filter(Boolean);
  const invoiceId = segments[1];
  const token = new URLSearchParams(window.location.search).get('t');

  useEffect(() => {
    if (!invoiceId || !token) { setErr('Invalid link.'); return; }
    api(`/api/portal/invoices/${invoiceId}?t=${encodeURIComponent(token)}`).then(setData).catch(e => setErr(e.message));
  }, [invoiceId, token]);

  const pay = async () => {
    setBusy(true);
    try {
      const r = await api(`/api/portal/invoices/${invoiceId}/pay?t=${encodeURIComponent(token)}`, { method:'POST' });
      if (r.url) window.location.href = r.url;
    } catch (e) {
      setErr(e.message);
    } finally { setBusy(false); }
  };

  if (err) return <div style={page}><div style={{ ...styles.card, padding:30, textAlign:'center' }}><h2>Can't open invoice</h2><p style={{ color:G.textMuted }}>{err}</p></div></div>;
  if (!data) return <div style={page}><Spinner sz={36}/></div>;

  const paid = data.status === 'paid';
  return <div style={page}>
    <div style={{ ...styles.card, padding:30, maxWidth:680, width:'100%' }}>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'baseline' }}>
        <div>
          <div style={{ fontSize:11, fontWeight:700, color:G.textMuted, textTransform:'uppercase', letterSpacing:'.08em' }}>{data.business?.name}</div>
          <h2 style={{ margin:'4px 0' }}>Invoice {data.num}</h2>
          <div style={{ color:G.textMuted, fontSize:13 }}>For: {data.customer?.firstName} {data.customer?.lastName}</div>
        </div>
        <div style={{ textAlign:'right' }}>
          <div style={{ fontSize:11, fontWeight:700, color:G.textMuted, textTransform:'uppercase', letterSpacing:'.08em' }}>Total Due</div>
          <div style={{ fontSize:34, fontWeight:800, color: paid ? G.green : G.text }}>${data.total.toFixed(2)}</div>
          {paid && <div style={{ color:G.green, fontSize:13, fontWeight:700, marginTop:2 }}>✓ PAID</div>}
        </div>
      </div>

      <div style={{ marginTop:22, padding:'14px 0', borderTop:`1px solid ${G.border}`, borderBottom:`1px solid ${G.border}` }}>
        {data.lines.map((l,i) => <div key={i} style={{ display:'grid', gridTemplateColumns:'1fr 60px 90px 90px', gap:10, padding:'6px 0', fontSize:13 }}>
          <div>{l.description || l.name}</div>
          <div style={{ textAlign:'right', color:G.textMuted }}>×{l.qty || 1}</div>
          <div style={{ textAlign:'right', color:G.textMuted }}>${Number(l.price||0).toFixed(2)}</div>
          <div style={{ textAlign:'right', fontWeight:600 }}>${((Number(l.qty)||1) * (Number(l.price)||0)).toFixed(2)}</div>
        </div>)}
      </div>

      <div style={{ marginTop:14, fontSize:13 }}>
        <Row k="Subtotal" v={`$${data.subtotal.toFixed(2)}`}/>
        <Row k={`Tax (${data.taxRate}%)`} v={`$${data.tax.toFixed(2)}`}/>
        {data.tip > 0 && <Row k="Tip" v={`$${data.tip.toFixed(2)}`}/>}
        <Row k={<strong>Total</strong>} v={<strong>${data.total.toFixed(2)}</strong>}/>
      </div>

      {!paid && data.canPay && <Btn v="primary" sz="lg" full disabled={busy} onClick={pay} style={{ marginTop:20 }}>
        {busy ? <Spinner/> : `Pay $${data.total.toFixed(2)} with Card or Bank`}
      </Btn>}

      {/* Financing offered on every unpaid invoice. The panel self-hides
          when no partner's amount range matches (e.g. tiny totals under $500
          where no lender will fund). 60-second app, soft-pull. */}
      {!paid && <InvoiceFinancingPanel invoiceId={invoiceId} token={token} total={data.total} business={data.business}/>}

      {!paid && !data.canPay && <div style={{ marginTop:20, padding:14, background:G.input, borderRadius:8, color:G.textMuted, fontSize:13, textAlign:'center' }}>
        Online payment not enabled. Contact {data.business?.name} to pay.
      </div>}

      <div style={{ marginTop:24, fontSize:11, color:G.textDim, textAlign:'center' }}>
        Powered by TradeOps · Stripe-secured
      </div>
    </div>
  </div>;
}

// Customer-facing financing offer on the pay page. Shows monthly-payment
// preview from the recommended partner + Apply button → opens partner-hosted
// 60s application. When funded, the financing webhook flips the invoice to paid.
function InvoiceFinancingPanel({ invoiceId, token, total, business }) {
  const [quote, setQuote] = useState(null);
  const [applying, setApplying] = useState(false);
  useEffect(() => {
    api(`/api/portal/invoices/${invoiceId}/financing-quote?t=${encodeURIComponent(token)}`).then(setQuote).catch(()=>{});
  }, [invoiceId, token]);
  if (!quote || !quote.partners?.length) return null;
  const rec = quote.partners.find(p => p.isRecommended) || quote.partners[0];
  const bestPayment = rec.payments?.find(p => p.termMonths === 60) || rec.payments?.[rec.payments.length-1];
  const apply = async () => {
    setApplying(true);
    try {
      const r = await api(`/api/portal/invoices/${invoiceId}/finance?t=${encodeURIComponent(token)}`, { method:'POST' });
      if (r.partnerUrl) window.location.href = r.partnerUrl;
    } catch(e) { alert(e.message); } finally { setApplying(false); }
  };
  return <div style={{ marginTop:18, padding:18, borderRadius:12, border:`2px solid ${G.green}66`, background:`linear-gradient(135deg, ${G.greenLight}, transparent)` }}>
    <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:8 }}>
      <span style={{ fontSize:22 }}>💳</span>
      <h3 style={{ margin:0, fontSize:16 }}>Or finance it</h3>
      <span style={{ marginLeft:'auto', fontSize:11, color:G.textMuted }}>60-sec approval · soft credit pull only</span>
    </div>
    {bestPayment && <div style={{ fontSize:14, color:G.text, marginBottom:12 }}>
      As low as <strong style={{ fontSize:24, color:G.green }}>${bestPayment.monthlyPaymentAllIn.toFixed(0)}/mo</strong>
      <span style={{ color:G.textMuted, fontSize:13 }}> for {bestPayment.termMonths} months</span>
      <span style={{ color:G.textMuted, fontSize:12, display:'block', marginTop:2 }}>via {rec.label}</span>
    </div>}
    <Btn v="primary" full onClick={apply} disabled={applying}>
      {applying ? <Spinner/> : `Apply for Financing · $${total.toFixed(0)}`}
    </Btn>
    <div style={{ fontSize:10, color:G.textMuted, textAlign:'center', marginTop:6 }}>
      Subject to credit approval. Rates vary. No prepayment penalty.
    </div>
  </div>;
}

function Row({ k, v }) {
  return <div style={{ display:'flex', justifyContent:'space-between', padding:'3px 0' }}>
    <span style={{ color:G.textMuted }}>{k}</span>
    <span>{v}</span>
  </div>;
}

const page = {
  minHeight:'100vh', display:'flex', alignItems:'center', justifyContent:'center', padding:24,
  background:`radial-gradient(circle at 30% 20%, ${G.purpleDim}, transparent 50%), ${G.bg}`,
};
